import React, { ReactNode } from 'react';
import { variables } from 'src/Tenants';
import Col from 'src/components/Col';
import Row from 'src/components/Row';
import Button from 'src/components/Button';
import FontAwesomeIcon from 'src/components/Icon';
import AnnotationImage from 'src/components/AnnotateImage/AnnotationImage';
import ServiceRequestFormDialog from '../ServiceRequestFormDialog';
import DetailsInfoFields from './components/DetailsInfoFields/DetailsInfoFields';
import DetailsTagsField from './components/DetailsTagsField/DetailsTagsField';
import DetailsAttachments from './components/DetailsAttachments/DetailsAttachments';
import DetailsScheduling from './components/DetailsScheduling/DetailsScheduling';
import DetailsSubmitFooter from './components/DetailsSubmitFooter/DetailsSubmitFooter';
import {
  useServiceRequestFormDetailsOrchestrator,
  UseServiceRequestFormDetailsOrchestratorReturn,
} from './useServiceRequestFormDetailsOrchestrator';
import {
  useAnnotationFormState,
  UseAnnotationFormStateReturn,
} from './ServiceRequestFormDetailsCustomHooks/useAnnotationFormState';
import {
  useAddNoteSrFormState,
  UseAddNoteSrFormStateReturn,
} from './ServiceRequestFormDetailsCustomHooks/useAddNoteSrFormState';
import ModernizedNoteForm from 'src/components/Notes/ModernizedNoteForm';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import Callout from 'src/components/InformationCallout/Callout';
import GenericDivider from 'src/components/GenericDivider/GenericDivider';
import {
  ADD_NOTE_CALLOUT_TITLE,
  ADD_NOTE_CALLOUT_SUBTITLE,
  ADD_NOTE_FORM_CALLOUT_TITLE,
  ADD_NOTE_FORM_CALLOUT_SUBTITLE,
  buildNotePreviewText,
  getHeaderCopy,
  DetailsHeaderCopy,
} from './ServiceRequestFormDetailsHelpers';
import './ServiceRequestFormDetails.scss';

const ServiceRequestFormDetails = () => {
  const {
    diagnosisAsset,
    diagnosisIssueType,
    warrantyExpiresAt,
    propertyName,
    suiteNumber,
    descriptionPlaceholder,
    description,
    onChangeDescription,
    canManageTags,
    tagOptions,
    selectedTags,
    onChangeTags,
    onSelectFiles,
    isAttachmentRequired,
    attachments,
    onDeleteAttachment,
    maxAttachments,
    canAddMoreAttachments,
    canAnnotate,
    annotatableAttachments,
    showScheduling,
    onChangeSchedule,
    primarySubmitLabel,
    isSubmitting,
    isSubmitDisabled,
    onSubmit,
    onBack,
    onExit,
  }: UseServiceRequestFormDetailsOrchestratorReturn =
    useServiceRequestFormDetailsOrchestrator();

  const {
    isAnnotating,
    annotations,
    onStartAnnotating,
    onExitAnnotating,
    onAnnotateImage,
    showRemoveAnnotationConfirm,
    onRequestRemoveAnnotation,
    onConfirmRemoveAnnotation,
    onCancelRemoveAnnotation,
  }: UseAnnotationFormStateReturn = useAnnotationFormState();

  const {
    isAddingNote,
    note,
    hasNote,
    onStartAddingNote,
    onExitAddingNote,
    onSaveNote,
    onRemoveNote,
  }: UseAddNoteSrFormStateReturn = useAddNoteSrFormState();

  const isEditingNote: boolean = isAddingNote && hasNote;

  const isInSubFlow: boolean = isAnnotating || isAddingNote;

  const headerCopy: DetailsHeaderCopy = getHeaderCopy(
    isAnnotating,
    isAddingNote,
    isEditingNote,
    variables.serviceRequest
  );

  const notePreviewText: string = note ? buildNotePreviewText(note) : '';

  const backLabel: string | undefined = isInSubFlow
    ? `Back to ${variables.serviceRequest}`
    : undefined;

  const onBackFromDetails = (): void => {
    if (isAnnotating) {
      onExitAnnotating();
      return;
    }

    if (isAddingNote) {
      onExitAddingNote();
      return;
    }

    onBack();
  };

  const footer: ReactNode = isInSubFlow ? undefined : (
    <DetailsSubmitFooter
      primarySubmitLabel={primarySubmitLabel}
      isSubmitting={isSubmitting}
      isSubmitDisabled={isSubmitDisabled}
      onSubmit={(type: string) => onSubmit(type, annotations, note ?? undefined)}
    />
  );

  return (
    <ServiceRequestFormDialog
      title={headerCopy.title}
      description={headerCopy.description}
      onBack={onBackFromDetails}
      backLabel={backLabel}
      onExit={onExit}
      footer={footer}
    >
      {isAnnotating && (
        <AnnotationImage
          images={annotatableAttachments}
          annotations={annotations}
          onAnnotate={onAnnotateImage}
          onRequestRemoveAnnotation={onRequestRemoveAnnotation}
          showRemoveAnnotationConfirm={showRemoveAnnotationConfirm}
          onConfirmRemoveAnnotation={onConfirmRemoveAnnotation}
          onCancelRemoveAnnotation={onCancelRemoveAnnotation}
        />
      )}

      {isAddingNote && (
        <>
          <Callout
            variant="info"
            title={ADD_NOTE_FORM_CALLOUT_TITLE}
            subtitle={ADD_NOTE_FORM_CALLOUT_SUBTITLE}
          />
          <ModernizedNoteForm
            saving={false}
            showHomeownerVisibilityCheckbox={false}
            showImageUpload={false}
            largeDescription={true}
            addLabel="Attach note to service request"
            editLabel="Update attached note"
            initialNote={hasNote ? note : undefined}
            onAddNote={async (noteTitle: string, noteBody: string) => {
              onSaveNote({ title: noteTitle, body: noteBody });
            }}
            onEditNote={async (noteTitle: string, noteBody: string) => {
              onSaveNote({ title: noteTitle, body: noteBody });
            }}
            onClose={onExitAddingNote}
          />
        </>
      )}

      {!isInSubFlow && (
        <div className="sr-form-details-content">
          <Row chonky>
            <Col chonky xs="12" md="6">
              <DetailsInfoFields
                diagnosisAsset={diagnosisAsset}
                diagnosisIssueType={diagnosisIssueType}
                warrantyExpiresAt={warrantyExpiresAt}
                propertyName={propertyName}
                suiteNumber={suiteNumber}
                descriptionPlaceholder={descriptionPlaceholder}
                description={description}
                onChangeDescription={onChangeDescription}
              />
              <DetailsTagsField
                canManageTags={canManageTags}
                tagOptions={tagOptions}
                selectedTags={selectedTags}
                onChangeTags={onChangeTags}
              />
            </Col>
            <Col chonky xs="12" md="6">
              <DetailsAttachments
                onSelectFiles={onSelectFiles}
                isAttachmentRequired={isAttachmentRequired}
                attachments={attachments}
                onDeleteAttachment={onDeleteAttachment}
                maxAttachments={maxAttachments}
                canAddMoreAttachments={canAddMoreAttachments}
                annotations={annotations}
              />
              {canAnnotate && (
                <div className="annotate-trigger">
                  <Button
                    color="complete_purple"
                    size="small"
                    icon={<FontAwesomeIcon glyph="pencil" weight="light" />}
                    label="Annotate images"
                    onClick={onStartAnnotating}
                    disabled={false}
                  />
                </div>
              )}
              {canAnnotate && <GenericDivider spacing="md" />}
              <Callout
                variant="info"
                title={ADD_NOTE_CALLOUT_TITLE}
                subtitle={ADD_NOTE_CALLOUT_SUBTITLE}
                className="add-note-callout"
              />
              <div className="add-note-trigger">
                <Button
                  color="complete_purple"
                  size="small"
                  icon={<FontAwesomeIcon glyph="edit" weight="light" />}
                  label={hasNote ? 'Edit note' : 'Add note'}
                  onClick={onStartAddingNote}
                  disabled={false}
                />
                {hasNote && (
                  <CategoryPill
                    text={notePreviewText}
                    iconGlyph="edit"
                    size="small"
                    trailingSlot={
                      <button
                        type="button"
                        className="add-note-pill-remove"
                        aria-label="Remove note"
                        onClick={onRemoveNote}
                      >
                        <FontAwesomeIcon glyph="times" weight="light" />
                      </button>
                    }
                  />
                )}
              </div>
              <DetailsScheduling
                show={showScheduling}
                onChangeSchedule={onChangeSchedule}
              />
            </Col>
          </Row>
        </div>
      )}
    </ServiceRequestFormDialog>
  );
};

export default ServiceRequestFormDetails;